import {
  add,
  differenceInCalendarYears,
  differenceInDays,
  differenceInMinutes,
  differenceInSeconds,
  format,
  isBefore,
  isSameDay,
  isValid,
  parse,
  sub,
  isToday,
  isTomorrow,
  startOfDay,
  isSameMonth,
  set,
} from "date-fns";
import { useEffect, useState } from "react";

export const getHoursMinutesFromHHMM = (timeString: string) => {
  const splitString = timeString.split(":");
  const hours = parseInt(splitString[0], 10);
  const minutes = parseInt(splitString[1], 10);
  return {
    hours,
    minutes,
  };
};

export const convertHoursMinutesToTimeString = (
  hours: number,
  minutes: number,
  seconds?: number
) => {
  const newHourString = hours.toString().length > 1 ? hours : `0${hours}`;
  const newMinuteString =
    minutes.toString().length > 1 ? minutes : `0${minutes}`;
  const newSecondsString = seconds
    ? seconds.toString().length > 1
      ? seconds
      : `0${seconds}`
    : "00";
  const newTime = `${newHourString}:${newMinuteString}:${newSecondsString}`;
  return newTime;
};

/**
 *
 * @param timeString string in HH:mm:ss format
 * @param meridian Do you want am/pm ?
 * @returns formatted string in "h:mm aaaa" format or "h:mm" format without
 */
export const formatHHMM = (
  timeString: string,
  meridian: boolean = true,
  amPmSpace = true
) => {
  if (!isValid(parse(timeString, "HH:mm:ss", new Date()))) {
    return null;
  }

  const date = parse(timeString, "HH:mm:ss", new Date());
  const dateString = meridian ? `h:mm${amPmSpace ? " " : ""}aaa` : "h:mm";
  return format(date, dateString);
};

export const formatHHMMSS = (timeString: string) => {
  if (!isValid(parse(timeString, "HH:mm:ss", new Date()))) {
    return null;
  }

  return format(parse(timeString, "HH:mm:ss", new Date()), "HH:mm:ss");
};

export const convertTo24HourString = (timeString: string) => {
  if (!isValid(parse(timeString, "H:MM", new Date()))) {
    return null;
  }

  const date = parse(timeString, "HH:mm:ss", new Date());
  return format(date, "H-MM");
};

/**
 *
 * Validates that a date string is both in our preferred 'yyyy-MM-dd' format
 * and is also parsable as a real date
 */
export const isDateStringValid = (dateString: string | null | undefined) => {
  return !!dateString && isValid(parse(dateString, "yyyy-MM-dd", new Date()));
};

/**
 * Ensures a server-provided date string is safe for date inputs.
 * Returns a fallback value when date is null/undefined/invalid.
 */
export const sanitizeDateValue = (
  dateString: string | null | undefined,
  fallback = ""
) => {
  return isDateStringValid(dateString) && dateString ? dateString : fallback;
};

/**
 *
 * We take a date string, split it into its relevant sections and assume it's UTC
 * so that we can convert it to local time.
 */
export const convertServerTimeToLocal = (date: string) => {
  let formattedDate;

  if (date.includes("Z")) {
    // Input already has timezone info
    formattedDate = date;
  } else if (date.includes("T")) {
    // Input has a 'T' but no timezone info
    formattedDate = `${date}.000Z`;
  } else {
    // Input has neither 'T' nor timezone info
    const [datePart, timePart] = date.split(" ");
    formattedDate = `${datePart}T${timePart}.000Z`;
  }

  return new Date(formattedDate);
};

/**
 * Parse an agenda date/time without applying the server-timezone conversion.
 * Agenda times can cross midnight, so the agenda-local date is authoritative.
 */
export const convertAgendaDateTimeToDate = (dateTime: string) => {
  if (!dateTime.includes(" ") && !dateTime.includes("T")) {
    return parse(dateTime, "HH:mm:ss", new Date());
  }

  const [datePart, rawTimePart = "00:00:00"] = dateTime.split(/[T ]/);
  const timePart = rawTimePart.replace(/\.\d+Z?$/, "").replace(/Z$/, "");

  return parse(
    `${datePart} ${timePart.slice(0, 8)}`,
    "yyyy-MM-dd HH:mm:ss",
    new Date()
  );
};

export const convertUTCDateObjToLocalTimezone = (date: Date) => {
  const milliseconds = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  );
  const localDateObj = new Date(milliseconds);
  return localDateObj;
};

/**
 * Hook that handles automatically updating components when the date changes.
 * Will pass new value when date changes.
 * @returns curDate: 'yyyy-MM-dd'
 */
export const useTodaysDate = () => {
  const date = format(new Date(), "yyyy-MM-dd");
  const [state, setState] = useState({ curDate: date });
  useEffect(() => {
    const interval = setInterval(() => {
      const curDate = format(new Date(), "yyyy-MM-dd");
      if (curDate !== state.curDate) {
        setState({ ...state, curDate: curDate });
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [state]);
  return state.curDate;
};
/**
 * Hook that will ingest a datestring and return whether that date string
 * is today.
 * @param dateString "yyyy-MM-dd"
 * @returns boolean;
 */
export const useIsToday = (dateString: string) => {
  const today = useTodaysDate();
  const parsedToday = parse(today, "yyyy-MM-dd", new Date());
  const date = parse(dateString, "yyyy-MM-dd", new Date());
  return isSameDay(parsedToday, date);
};

/**
 *
 * @param dateString "yyyy-MM-dd"
 * @returns whether the date is before today.
 */
export const useIsPastDate = (dateString: string) => {
  const today = useTodaysDate();
  const parsedToday = parse(today, "yyyy-MM-dd", new Date());
  const dateComp = parse(dateString, "yyyy-MM-dd", new Date());
  return isBefore(dateComp, parsedToday);
};

/**
 * gets the number of years from given date.
 */
export const yearsSinceDate = (dateString: string) => {
  const date = new Date(dateString);
  return differenceInCalendarYears(new Date(), date);
};

/**
 * gets the year from a given date
 */
export const getYearFromDate = (dateString: string) => {
  return parse(dateString, "yyyy-MM-dd", new Date()).getFullYear();
};

/**
 *
 * @param dateString yyyy-MM-dd format
 * @returns Date object after parsing string.
 */
export const convertDateStringToObj = (
  dateString: string | null | undefined
) => {
  if (!dateString || !isDateStringValid(dateString)) {
    throw new Error(
      `convertDateStringToObj must be called with ` +
        `string in yyyy-MM-dd format. Was instead called with ${dateString}`
    );
  }

  return parse(dateString, "yyyy-MM-dd", new Date());
};

/**
 *
 * @param date
 * @returns string in yyyy-MM-dd format.
 */
export const convertDateObjToServerStringFormat = (date: Date) => {
  return format(date, "yyyy-MM-dd");
};

export const convertDateTimeObjToServerStringFormat = (date: Date) => {
  return format(date, "yyyy-MM-dd HH:mm:ss");
};

export const combineDateAndTimeToServerDateTime = (
  dateString: string,
  timeString: string
) => {
  const date = convertDateStringToObj(dateString);
  const { hours, minutes } = getHoursMinutesFromHHMM(timeString);
  const seconds = Number.parseInt(timeString.split(":")[2] ?? "0", 10);

  return convertDateTimeObjToServerStringFormat(
    set(date, {
      hours,
      minutes,
      seconds,
      milliseconds: 0,
    })
  );
};

export const getAgendaTimeOffsetMinutes = (
  timeString: string,
  agendaStartTime: string
) => {
  const timeDate = parse(timeString, "HH:mm:ss", new Date());
  const agendaStartDate = parse(agendaStartTime, "HH:mm:ss", new Date());

  if (!isValid(timeDate) || !isValid(agendaStartDate)) {
    return 0;
  }

  let diff = differenceInMinutes(timeDate, agendaStartDate);
  if (diff < 0) {
    diff += 24 * 60;
  }

  return diff;
};

export const compareAgendaTimes = (
  timeA: string,
  timeB: string,
  agendaStartTime: string
) => {
  return (
    getAgendaTimeOffsetMinutes(timeA, agendaStartTime) -
    getAgendaTimeOffsetMinutes(timeB, agendaStartTime)
  );
};

export const differenceInAgendaTimeMinutes = (
  startTime: string,
  endTime: string,
  agendaStartTime: string
) => {
  return (
    getAgendaTimeOffsetMinutes(endTime, agendaStartTime) -
    getAgendaTimeOffsetMinutes(startTime, agendaStartTime)
  );
};

export const formatServerDateTimeHHMM = (
  dateTimeString: string,
  meridian: boolean = true,
  amPmSpace = true
) => {
  const date = convertServerTimeToLocal(dateTimeString);
  if (!isValid(date)) {
    return null;
  }

  const dateString = meridian ? `h:mm${amPmSpace ? " " : ""}aaa` : "h:mm";
  return format(date, dateString);
};

export const formatAgendaDateTimeHHMM = (
  dateTimeString: string,
  meridian: boolean = true,
  amPmSpace = true
) => {
  const date = convertAgendaDateTimeToDate(dateTimeString);
  if (!isValid(date)) {
    return null;
  }

  const dateString = meridian ? `h:mm${amPmSpace ? " " : ""}aaa` : "h:mm";
  return format(date, dateString);
};

export const formatAgendaDateTimeHHMMWithAgendaDate = (
  dateTimeString: string,
  agendaDateString: string | null | undefined,
  meridian: boolean = true,
  amPmSpace = true
) => {
  const timeString = formatAgendaDateTimeHHMM(
    dateTimeString,
    meridian,
    amPmSpace
  );
  const slotDateString = dateTimeString.split(/[T ]/)[0];

  if (
    !timeString ||
    !isDateStringValid(slotDateString) ||
    !isDateStringValid(agendaDateString)
  ) {
    return timeString;
  }

  return slotDateString === agendaDateString
    ? timeString
    : `${timeString} (${format(
        convertDateStringToObj(slotDateString),
        "MMM d"
      )})`;
};

export const formatServerDateTimeHHMMWithAgendaDate =
  formatAgendaDateTimeHHMMWithAgendaDate;

export const compareServerDateTimes = (
  dateTimeA: string,
  dateTimeB: string
) => {
  return (
    convertServerTimeToLocal(dateTimeA).getTime() -
    convertServerTimeToLocal(dateTimeB).getTime()
  );
};

export const differenceInMinutesBetweenServerDateTimes = (
  startDateTime: string,
  endDateTime: string
) => {
  return differenceInMinutes(
    convertServerTimeToLocal(endDateTime),
    convertServerTimeToLocal(startDateTime)
  );
};

export const convertServerTimeString = (
  serverTimeString: string | null | undefined,
  formatCode: string
) => {
  let displayDate = null;
  if (serverTimeString) {
    const dateObj = convertServerTimeToLocal(serverTimeString);
    displayDate = format(dateObj, formatCode);
    return displayDate;
  } else return null;
};

export const getBeginningOfYear = () => {
  const date = new Date(new Date().getFullYear(), 0, 1);
  return format(date, "yyyy-MM-dd");
};

export const addMinutesToTimeString = (timeString: string, minutes: number) => {
  const date = parse(timeString, "HH:mm:ss", new Date());
  const time = format(date, "HH:mm:ss");
  if (time) {
    let dateObj = parse(time, "HH:mm:ss", new Date());
    if (minutes > 0) {
      dateObj = add(dateObj, {
        minutes,
      });
    }

    if (minutes < 0) {
      dateObj = sub(dateObj, {
        minutes: Math.abs(minutes),
      });
    }

    return format(dateObj, "HH:mm:ss");
  }

  return timeString;
};

export const getUnlocksInValues = (liftAt: string | null, now = new Date()) => {
  if (!liftAt) {
    return {
      daysLeft: 0,
      hoursLeft: 0,
      minutesLeft: 0,
      secondsLeft: 0,
    };
  }

  const unlocksOnDateObj = convertServerTimeToLocal(liftAt);
  if (!isBefore(now, unlocksOnDateObj)) {
    return {
      daysLeft: 0,
      hoursLeft: 0,
      minutesLeft: 0,
      secondsLeft: 0,
    };
  }

  const diffInMinutes = differenceInMinutes(unlocksOnDateObj, now);
  const daysLeft = Math.floor(diffInMinutes / (60 * 24));
  const hoursLeft = Math.floor((diffInMinutes - daysLeft * 60 * 24) / 60);
  const minutesLeft = diffInMinutes - daysLeft * 60 * 24 - hoursLeft * 60;
  const secondsLeft =
    daysLeft === 0 && hoursLeft === 0
      ? differenceInSeconds(unlocksOnDateObj, now) - minutesLeft * 60
      : 0;
  return { daysLeft, hoursLeft, minutesLeft, secondsLeft };
};

export const getUnlocksIn = (unlocksAt: string | null, now = new Date()) => {
  const { daysLeft, hoursLeft, minutesLeft, secondsLeft } = getUnlocksInValues(
    unlocksAt,
    now
  );

  const showOpeningText =
    daysLeft === 0 && hoursLeft === 0 && minutesLeft === 0 && secondsLeft <= 0;
  const showSeconds = daysLeft === 0 && hoursLeft === 0 && minutesLeft <= 59;
  return showOpeningText
    ? "Opening..."
    : showSeconds
    ? `${minutesLeft > 0 ? `${minutesLeft}m` : ""} ${secondsLeft}s`
    : `${daysLeft > 0 ? `${daysLeft}d ` : ""}${
        hoursLeft > 0 ? `${hoursLeft}hrs ` : ""
      }${minutesLeft > 0 ? `${minutesLeft}min` : ""}`;
};

/**
 *
 * @param startDate
 * @param endDate
 *
 * Returns every date between the two dates, does not include start or end
 */
export const getAllDatesBetweenTwoDays = (
  startDate: string,
  endDate: string
) => {
  const startDateObj = parse(startDate, "yyyy-MM-dd", new Date());
  const daysBetween = differenceInDays(
    parse(endDate, "yyyy-MM-dd", new Date()),
    startDateObj
  );
  return daysBetween === 0
    ? []
    : Array(daysBetween - 1)
        .fill(0)
        .map((_, i) => {
          return format(add(startDateObj, { days: i + 1 }), "yyyy-MM-dd");
        });
};

/**
 * Returns time from now until a date in a readable string format
 */

export const getTimeUntilDateString = (date: string) => {
  const dateObj = parse(date, "yyyy-MM-dd HH:mm:ss", new Date());

  const localDate = convertUTCDateObjToLocalTimezone(dateObj);

  const timeString = format(localDate, "h:mm a");

  if (isToday(localDate)) return `${`today at`} ${timeString}`;

  if (isTomorrow(localDate)) return `${`tomorrow at`} ${timeString}`;

  const daysUntil = differenceInDays(
    startOfDay(localDate),
    startOfDay(new Date())
  );

  return `in ${daysUntil} ${`days at`} ${timeString}`;
};

/**
 * Returns a hours and minutes string when given minutes as a number.
 */

export const calcDurationString = (
  totalMinutes: number | null | undefined
): string => {
  if (!totalMinutes) {
    return "";
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours >= 1 ? hours + " hr" : ""}${
    hours >= 1 && minutes ? " " : ""
  }${minutes ? minutes + " min" : ""}`;
};

/**
 *
 * Takes two dates strings in yyyy-MM-dd format and returns whether they are in the same month
 */
export const areDateStringsSameMonth = (
  dateString1: string,
  dateString2: string
) => {
  return isSameMonth(
    parse(dateString1, "yyyy-MM-dd", new Date()),
    parse(dateString2, "yyyy-MM-dd", new Date())
  );
};
